/**
 * visitTiming.js — s101 (Dr. Rami, 2026-10-12): WHEN IS A VISIT PAST?
 *
 * ★★★ THE DEFECT (Build 65, Alex, case 8AC3FCD4). VisitsScreen kept every SAME-DAY
 *   appointment under Upcoming whatever the time (v193.13), and moved a row to Past only on
 *   status completed / finalized. The 1:00 PM visit was still "Upcoming" at 1:22 PM after
 *   the case was finalized at 1:11 PM — and since the API never completed a CONFIRMED row on
 *   finalize, it would have stayed there until midnight and then dropped to Past by date.
 * ★★★ HIS RULING: a visit is done when the case is finalized OR its time has passed.
 *   - finalized: the API now completes a confirmed visit of today or earlier on finalize
 *     (orella-api `_visit_has_happened`), so the row arrives here as `completed`.
 *   - time passed: start + duration + VISIT_GRACE_MINUTES (his choice, 1 hour).
 * ★★ There is no day key any more (PROD-1368): the old device-local date key was replaced by a
 *   true-instant comparison, so nothing here reads the device's calendar day.
 * ★★ PROD-1368: the visit's date + time are the practice's wall clock (ORELLA_TZ), so the end is a
 *   true instant from dateUtils.wallTimeToInstant, compared against the current instant. Reading
 *   them in the device zone moved a 1:00 PM visit to Past three hours late in New York.
 * Pure: no React, no store. scripts/check-visit-timing.mjs runs it.
 */

import { wallTimeToInstant } from './dateUtils.js'

export const VISIT_GRACE_MINUTES = 60
export const DEFAULT_VISIT_MINUTES = 30

const DONE = new Set(['completed', 'finalized', 'no_show'])

/** "9:30 AM" | "09:30" | "09:30:00" -> "09:30"; anything else -> null. */
export function to24Hour(timeStr) {
  const s = String(timeStr || '').trim()
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`
  m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m) return null
  let h = parseInt(m[1], 10)
  const p = m[3].toUpperCase()
  if (p === 'PM' && h !== 12) h += 12
  if (p === 'AM' && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

/** The moment a visit stops being upcoming, as a Date (a true instant; the slot is ORELLA_TZ wall time), or null. */
export function visitEndsAt(appt) {
  const day = String(appt?.date || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  // No usable time: the visit lasts the whole (practice) day, then the grace.
  const start = wallTimeToInstant(day, to24Hour(appt?.time) || '23:59')
  if (!start) return null
  const minutes = Number(appt?.duration) > 0 ? Number(appt.duration) : DEFAULT_VISIT_MINUTES
  return new Date(start.getTime() + (minutes + VISIT_GRACE_MINUTES) * 60000)
}

/** 'cancelled' | 'past' | 'upcoming' for one appointment at `now`. */
export function visitBucket(appt, now = Date.now()) {
  const status = String(appt?.status || '').toLowerCase()
  if (status === 'cancelled') return 'cancelled'
  if (DONE.has(status)) return 'past'
  const ends = visitEndsAt(appt)
  if (!ends) return 'upcoming'
  return Number(now) >= ends.getTime() ? 'past' : 'upcoming'
}
